import type { ReactNode } from "react";

import { Box, Tooltip } from "@mantine/core";

interface DisabledReasonProps {
  /** Why the wrapped control is disabled. Absent renders the control as is. */
  reason?: string;
  /** For controls that fill their row, such as menu items and inputs. */
  isBlock?: boolean;
  /** Passed to the tooltip. */
  "data-testid"?: string;
  children: ReactNode;
}

/**
 * Explains why a control is disabled. A disabled control fires no pointer events, so the tooltip
 * listens on a focusable wrapper around it instead, and opens on hover, keyboard focus, or a tap
 * on a touch screen, where a hover-only tooltip never shows.
 */
export default function DisabledReason({
  reason,
  isBlock = false,
  "data-testid": dataTestId,
  children,
}: DisabledReasonProps) {
  if (!reason) return <>{children}</>;

  return (
    <Tooltip
      maw={260}
      multiline
      label={reason}
      data-testid={dataTestId}
      events={{ hover: true, focus: true, touch: true }}
    >
      <Box tabIndex={0} component="span" display={isBlock ? "block" : "inline-block"}>
        {children}
      </Box>
    </Tooltip>
  );
}
